/**
 * useNotifications — in-app notification feed. Carries both human-
 * collaboration kinds (docs/COLLABORATION_CONTRACT.md §3: `lead_assigned` +
 * invite/share/role events) and AI virtual-staff kinds (`mas_persona_action`,
 * `staff_decision_needed`, `staff_escalation`, `staff_delivery` — see
 * lib/api/notifications.ts's NOTIFICATION_KIND_INFO catalog), polled on an
 * interval so the bell badge stays fresh without a websocket. Optimistic
 * mark-read/mark-all-read (the row flips instantly; a failure reconciles by
 * refetching). The returned `items`/`unreadCount` are already filtered by the
 * owner's client-side NotificationPrefs (localStorage kind toggles, default
 * all-on) — see `./NotificationPrefs.tsx`.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  type AppNotification,
  countUnread,
  listNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from '@/lib/api/notifications';
import { isKindSurfaced, useNotificationKindPrefs } from './NotificationPrefs';

const DEFAULT_POLL_MS = 30_000;
const PAGE_SIZE = 20;

export interface UseNotifications {
  items: AppNotification[];
  unreadCount: number;
  loading: boolean;
  error: boolean;
  refresh: () => Promise<void>;
  markRead: (id: string) => Promise<void>;
  markAllRead: () => Promise<void>;
}

/** `pollMs <= 0` disables polling (used by tests). */
export function useNotifications(pollMs: number = DEFAULT_POLL_MS): UseNotifications {
  const [items, setItems] = useState<AppNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const mountedRef = useRef(true);

  const load = useCallback(async () => {
    try {
      const res = await listNotifications({ limit: PAGE_SIZE });
      if (!mountedRef.current) return;
      setItems(res.items);
      setUnreadCount(res.unread_count ?? countUnread(res.items));
      setError(false);
    } catch {
      if (mountedRef.current) setError(true);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    void load();
    const timer = pollMs > 0 ? window.setInterval(() => void load(), pollMs) : undefined;
    return () => {
      mountedRef.current = false;
      if (timer !== undefined) window.clearInterval(timer);
    };
  }, [load, pollMs]);

  const markRead = useCallback(
    async (id: string) => {
      // Read "was it unread" from the current `items` closure BEFORE queuing
      // the state update — a `setState(prev => ...)` updater runs during
      // React's render phase, not synchronously at call time, so a side
      // effect written inside it (e.g. `wasUnread = true`) is not yet visible
      // to code immediately following the `setItems(...)` call.
      const wasUnread = items.some((n) => n.id === id && !n.read);
      setItems((prev) =>
        prev.map((n) =>
          n.id === id ? { ...n, read: true, read_at: new Date().toISOString() } : n,
        ),
      );
      if (wasUnread) setUnreadCount((c) => Math.max(0, c - 1));
      try {
        await markNotificationRead(id);
      } catch {
        // Reconcile with the server rather than leaving an inconsistent badge.
        await load();
      }
    },
    [items, load],
  );

  const markAllRead = useCallback(async () => {
    const unreadIds = items.filter((n) => !n.read).map((n) => n.id);
    if (unreadIds.length === 0) return;
    setItems((prev) => prev.map((n) => (n.read ? n : { ...n, read: true })));
    setUnreadCount(0);
    const failed = await markAllNotificationsRead(unreadIds);
    if (failed.length > 0) await load();
  }, [items, load]);

  // Client-side kind prefs (NotificationPrefs, localStorage-only — see that
  // file's header) suppress hidden kinds from BOTH the list and the badge, so
  // muting a kind really means "don't surface it in the bell" rather than
  // just hiding rows while the count still includes them. The common case
  // (no overrides — everything on) is a pure passthrough of the raw state, so
  // this changes nothing when the owner never opens the prefs panel.
  const { prefs } = useNotificationKindPrefs();
  const hasHiddenKinds = useMemo(() => Object.values(prefs).some((on) => on === false), [prefs]);
  const visibleItems = useMemo(
    () => (hasHiddenKinds ? items.filter((n) => isKindSurfaced(prefs, n.kind ?? n.type)) : items),
    [items, prefs, hasHiddenKinds],
  );
  const visibleUnreadCount = hasHiddenKinds ? countUnread(visibleItems) : unreadCount;

  return {
    items: visibleItems,
    unreadCount: visibleUnreadCount,
    loading,
    error,
    refresh: load,
    markRead,
    markAllRead,
  };
}
